import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../../../api/api';

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'https://kandor.org';
const isLocalSocket = /localhost|127\.0\.0\.1/.test(SOCKET_URL);
const SOCKET_PATH = isLocalSocket ? '/socket.io/' : '/api/socket.io/';

/**
 * Custom hook for managing the KODA counselor SocketIO connection.
 * Handles connection lifecycle, message sending, streaming responses,
 * pending actions (confirmation cards), and follow-up questions.
 */
export default function useKodaSocket() {
  const socketRef = useRef(null);
  const hasConnectedRef = useRef(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [actionResult, setActionResult] = useState(null);

  // Track current msgId for streaming correlation
  const currentMsgIdRef = useRef(null);

  /**
   * Get the Clerk user ID from the global Clerk object
   */
  const getUserId = useCallback(() => {
    if (window.Clerk?.user?.id) return window.Clerk.user.id;
    if (window.Clerk?.session?.user?.id) return window.Clerk.session.user.id;
    return null;
  }, []);

  /**
   * Connect to SocketIO server
   */
  const connect = useCallback(() => {
    if (socketRef.current) return;

    const socket = io(SOCKET_URL, {
      path: SOCKET_PATH,
      withCredentials: true,
      // transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socket.on('connect', () => {
      console.log('[KodaSocket] Connected:', socket.id);
      hasConnectedRef.current = true;
      setConnectionStatus('connected');

      // Emit counselor-specific join
      socket.emit('counselor:connect');
      const userId = getUserId();
      if (userId) {
        socket.emit('counselor:join', { userId });
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('[KodaSocket] Disconnected:', reason);
      if (reason === 'io server disconnect') {
        setConnectionStatus('offline');
        return;
      }
      setConnectionStatus('reconnecting');
    });

    socket.on('connect_error', (err) => {
      console.error('[KodaSocket] Connection error:', err.message);
      setConnectionStatus(
        hasConnectedRef.current ? 'reconnecting' : 'connecting',
      );
    });

    socket.io.on('reconnect_attempt', () => {
      setConnectionStatus('reconnecting');
    });

    socket.io.on('reconnect', () => {
      setConnectionStatus('connected');
    });

    socket.io.on('reconnect_failed', () => {
      console.error('[KodaSocket] Reconnection failed');
      setConnectionStatus('offline');
    });

    // --- Counselor bot events ---

    socket.on('counselor:connected', (data) => {
      console.log('[KodaSocket] Counselor connected:', data);
    });

    socket.on('counselor:joined', (data) => {
      console.log('[KodaSocket] Counselor joined room:', data);
    });

    // Streaming tokens
    socket.on('counselor:chat_response', (data) => {
      if (data.msgId === currentMsgIdRef.current) {
        setStreamingText(data.data || '');
      }
    });

    // Real-time tool activity (from agent's on_tool_start / on_tool_end)
    socket.on('counselor:tool_activity', (data) => {
      console.log('[KodaSocket] Tool activity event:', data);
      if (data.status === 'running') {
        setThinkingSteps((prev) => {
          // Add new step if not already present
          const label = data.label || data.tool;
          if (prev.some((s) => s.label === label)) return prev;
          return [...prev, { label, status: 'running' }];
        });
      } else if (data.status === 'done') {
        setThinkingSteps((prev) => {
          // Mark the last running step as done
          const updated = [...prev];
          const runningIdx = updated.findIndex((s) => s.status === 'running');
          if (runningIdx !== -1) {
            updated[runningIdx] = { ...updated[runningIdx], status: 'done' };
          }
          return updated;
        });
      }
    });

    // Pending action (confirmation card)
    socket.on('counselor:pending_action', (data) => {
      console.log('[KodaSocket] Pending action:', data);
      setPendingAction({
        msgId: data.msgId,
        actionType: data.action_type,
        preview: data.preview || {},
        editableFields: data.editable_fields || [],
        resolvedParams: data.resolved_params || {},
      });
    });

    // Follow-up questions
    socket.on('counselor:follow_up_questions', (data) => {
      console.log('[KodaSocket] Follow-ups:', data);
      setFollowUps(data.questions || []);
    });

    // Chat complete
    socket.on('counselor:chat_done', (data) => {
      console.log('[KodaSocket] Chat done:', data);
      if (data.msgId === currentMsgIdRef.current) {
        const finalText = data.data || '';

        setMessages((prev) => {
          // Replace the streaming placeholder with the final message
          const updated = prev.filter((m) => m.id !== `streaming-${data.msgId}`);
          return [
            ...updated,
            {
              id: data.msgId + '-response',
              role: 'assistant',
              content: finalText,
              timestamp: new Date().toISOString(),
              hasPendingAction: data.has_pending_action,
              structuredData: {
                filter_params: data.filter_params,
                task_id: data.task_id,
                meeting_id: data.meeting_id,
              },
            },
          ];
        });

        setStreamingText('');
        setIsLoading(false);
        setThinkingSteps([]);
        currentMsgIdRef.current = null;
      }
    });

    // Action result (after confirm)
    socket.on('counselor:action_result', (data) => {
      console.log('[KodaSocket] Action result:', data);
      setActionResult(data);
      setPendingAction(null);

      // Add a system message about the action result
      setMessages((prev) => [
        ...prev,
        {
          id: data.msgId + '-action',
          role: 'system',
          content: data.success
            ? `Action "${data.action_type}" completed successfully.`
            : `Action "${data.action_type}" failed: ${data.result?.error || 'Unknown error'}`,
          type: 'action_result',
          success: data.success,
          actionType: data.action_type,
          timestamp: new Date().toISOString(),
        },
      ]);
    });

    // Action cancelled
    socket.on('counselor:action_cancelled', (data) => {
      console.log('[KodaSocket] Action cancelled:', data);
      setPendingAction(null);
      setMessages((prev) => [
        ...prev,
        {
          id: data.msgId + '-cancelled',
          role: 'system',
          content: data.message || 'Action cancelled.',
          type: 'action_cancelled',
          timestamp: new Date().toISOString(),
        },
      ]);
    });

    // Error
    socket.on('counselor:chat_error', (data) => {
      console.error('[KodaSocket] Chat error:', data);
      setIsLoading(false);
      setStreamingText('');
      currentMsgIdRef.current = null;

      setMessages((prev) => [
        ...prev,
        {
          id: (data.msgId || Date.now()) + '-error',
          role: 'system',
          content: `Error: ${data.error || 'Something went wrong'}`,
          type: 'error',
          timestamp: new Date().toISOString(),
        },
      ]);
    });

    socketRef.current = socket;
  }, [getUserId]);

  /**
   * Send a chat message
   */
  const sendMessage = useCallback(
    (text, kodaContext) => {
      if (!text.trim() || !socketRef.current?.connected) return;

      const userId = getUserId();
      if (!userId) {
        console.error('[KodaSocket] No user ID available');
        return;
      }

      const msgId = crypto.randomUUID?.() || `msg-${Date.now()}`;
      currentMsgIdRef.current = msgId;

      // Add user message to list
      setMessages((prev) => [
        ...prev,
        {
          id: msgId,
          role: 'user',
          content: text,
          timestamp: new Date().toISOString(),
        },
      ]);

      // Reset state for new message
      setIsLoading(true);
      setStreamingText('');
      setPendingAction(null);
      setFollowUps([]);
      setActionResult(null);
      setThinkingSteps([]);

      // Build conversation history from previous messages (last 10)
      const recentHistory = messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));

      const payload = {
        userId,
        data: text,
        msgId,
        conversation_history: recentHistory,
      };
      if (kodaContext && Object.keys(kodaContext).length) {
        payload.kodaContext = kodaContext;
      }

      socketRef.current.emit('counselor:chat_message', payload);
    },
    [getUserId, messages]
  );

  /**
   * Confirm a pending action with (possibly edited) parameters
   */
  const confirmAction = useCallback(
    (actionType, parameters) => {
      if (!socketRef.current?.connected) return;

      const userId = getUserId();
      if (!userId) return;

      const msgId = pendingAction?.msgId || crypto.randomUUID?.() || `confirm-${Date.now()}`;

      socketRef.current.emit('counselor:confirm_action', {
        userId,
        msgId,
        action_type: actionType,
        parameters,
      });
    },
    [getUserId, pendingAction]
  );

  /**
   * Cancel a pending action
   */
  const cancelAction = useCallback(
    (actionType) => {
      if (!socketRef.current?.connected) return;

      const msgId = pendingAction?.msgId || crypto.randomUUID?.() || `cancel-${Date.now()}`;

      socketRef.current.emit('counselor:cancel_action', {
        msgId,
        action_type: actionType || pendingAction?.actionType || 'unknown',
      });
    },
    [pendingAction]
  );

  /**
   * Clear chat history
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    setStreamingText('');
    setPendingAction(null);
    setFollowUps([]);
    setActionResult(null);
    setThinkingSteps([]);
  }, []);

  // Load persisted history and connect on mount, disconnect on unmount
  useEffect(() => {
    connect();

    api
      .get('/counselor/aibot/history')
      .then((res) => {
        const saved = res?.data?.messages;
        if (Array.isArray(saved) && saved.length > 0) {
          setMessages(
            saved
              .filter((m) => m.role === 'user' || m.role === 'assistant')
              .map((m, i) => ({
                id: `history-${i}`,
                role: m.role,
                content: m.content,
                timestamp: m.timestamp || null,
              }))
          );
        }
      })
      .catch((err) => console.warn('[KodaSocket] Could not load history:', err?.message));

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      hasConnectedRef.current = false;
    };
  }, [connect]);

  const isChatUnavailable = connectionStatus === 'offline';

  return {
    connectionStatus,
    isChatUnavailable,
    messages,
    isLoading,
    streamingText,
    pendingAction,
    followUps,
    thinkingSteps,
    actionResult,
    sendMessage,
    confirmAction,
    cancelAction,
    clearChat,
  };
}
